import { ICompany } from '@collections/companies/interfaces/company.interface';
import { IProduct } from '@collections/products/interfaces/product.interface';
import { AuthService } from '@shared/auth/auth.service';
/* import { CookieService } from 'ngx-cookie-service'; */
import { LazyLoadService } from 'app/lazy-load/lazy-load.service';
import { ApplicationRef, createComponent, EmbeddedViewRef, Inject, Injectable, LOCALE_ID, NgZone } from '@angular/core';
// import { WindowToken } from '@shared/tokens/window';
import { DOCUMENT } from '@angular/common';
import { forkJoin, Observable, of, tap, Subject, ReplaySubject, Subscription, filter, bufferTime, groupBy, mergeMap, toArray, from, catchError } from 'rxjs';
// import { GA4_ID, GMT_ID } from '@shared/tokens/GMT';
import { PageMetadata } from '../../meta-data-service/meta-data.commons';
import { DataLayerCustomDimensions } from './data-layer-custom-dimensions';
import { Router } from '@angular/router';
import { INews } from '@collections/news/interfaces';
import { IElasticSearchItem } from '@server/elasticSearch/interfaces';
import { EsType } from '@server/elasticSearch/enums';
import { environment } from '@env/environment';
declare global {
  interface Window {
    dataLayer: any[];
    requestIdleCallback(callback: IdleRequestCallback, options?: IdleRequestOptions): number;
  }
  interface IdleRequestCallback {
    (deadline: IdleDeadline): void;
  }

  interface IdleDeadline {
    timeRemaining(): DOMHighResTimeStamp;
    readonly didTimeout: boolean;
  }

  interface IdleRequestOptions {
    timeout?: number | undefined;
  }
}

export class GA4EcommerceItem {
  item_list_id?: string;
  item_list_name?: string;
  promotion_id?: string;
  promotion_name?: string;
  item_id: string;
  item_name: string;
  item_brand: string;
  item_variant?: string;
  item_category?: string;
  item_category1?: string;
  item_category2?: string;
  item_category3?: string;
  item_category4?: string;
  item_category5?: string;
}

@Injectable()
export class AnalyticsService {
  public dataLayerCustomDimensions: DataLayerCustomDimensions;
  private trackPage$ = new ReplaySubject<PageMetadata>();
  private trackPageSubscription: Subscription;
  private queueSubject: Subject<GA4EcommerceItem> = new Subject();
  private hasConsent: boolean;
  private gmtId: string;
  private gmtIsLoaded = false;
  private userId: string;
  private userRoles: string[];


  constructor(
    // @Inject(WindowToken) private window: Window,
    @Inject(DOCUMENT) private document: Document,
    private lazyLoadService: LazyLoadService,
    /* private cookieService: CookieService, */
    private appRef: ApplicationRef,
    private router: Router,
    private authService: AuthService,
    @Inject(LOCALE_ID) private locale: string,
    private zone: NgZone
  ) {
    this.gmtId = environment.GTM_Id;
    // this.window.dataLayer = this.window.dataLayer || [{ 'gtm.start': new Date().getTime(), event: 'gtm.js', lang: locale, ga4id: environment.GA4_id, gtmserv: environment.gtmserv , dev: environment.debugOn}];
    window.dataLayer = window.dataLayer || [{ 'gtm.start': new Date().getTime(), event: 'gtm.js', lang: locale, ga4id: environment.GA4_id, gtmserv: environment.gtmserv , dev: environment.debugOn}];

    this.zone.runOutsideAngular(() => {
      this.queueSubject
      .pipe(
        bufferTime(2000),
        filter(items => items.length > 0),
      )
      .subscribe((items) => {
        from(items)
        .pipe(
          groupBy((item) => item.item_list_id),
          mergeMap((group) =>  group.pipe(toArray())),
        )
        .subscribe(items => {
          this.resetEc();
          window.dataLayer.push({
            event: 'view_item_list',
            ecommerce: {
              item_list_id: items[0].item_list_id,
              item_list_name: items[0].item_list_name,
              items: items
            }
          });
        })
      });
    });


    this.authService.authTokenChange
    .subscribe(token => {
      if (this.trackPageSubscription) {
        this.trackPageSubscription.unsubscribe();
      }
      
      this.userId = token ? token.id : this.generateTemporaryId() + '_guest';
      // console.log(this.userId);
      this.userRoles = token ? token.roles : ['none'];
      this.dataLayerCustomDimensions = new DataLayerCustomDimensions(this.userId, this.userRoles);
      this.trackPageSubscription = this.trackPage$
      .subscribe((pageMetadata: PageMetadata) => {
        this._track(pageMetadata);
      });
    });

/*  if ('requestIdleCallback' in window) {
      this.window.requestIdleCallback(() => this.loadCookieConsentModule());
    } else {
      this.loadCookieConsentModule();
    } */
  }

  generateTemporaryId(): string {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const idLength = 10; // Lunghezza desiderata dell'ID temporaneo

    let temporaryId = '';
    for (let i = 0; i < idLength; i++) {
        const randomIndex = Math.floor(Math.random() * characters.length);
        temporaryId += characters[randomIndex];
    }

    return temporaryId;
  }

  async trackPageView(pageMetadata: PageMetadata): Promise<void> {
    this.trackPage$.next(pageMetadata);
  }

  private _track(pageMetadata: PageMetadata): void {
    const dimensions = this.dataLayerCustomDimensions;
    try {
      dimensions.category = pageMetadata?.categories ? pageMetadata?.categories[0] : dimensions.category;
      dimensions.authorId = pageMetadata.authorId ?? dimensions.authorId;
      dimensions.pageType = pageMetadata.pageType ?? dimensions.pageType;
      dimensions.companySlug = pageMetadata.company?.slug ?? dimensions.companySlug;
    }
    catch (e) {
      console.log(e);
    }

    this.ensureGMT()
    .subscribe(() => {
      window.dataLayer.push({
      ...dimensions,
      event: 'virtualPageview',
      pageTitle: pageMetadata.title,
      pagePath: `/${this.locale}${pageMetadata.url || this.router.url}`,
      ecommerce: null,
      eventCallback : () => {
          // reset data
          this.dataLayerCustomDimensions = new DataLayerCustomDimensions(this.userId, this.userRoles);
        }
      });
    });
  }

  async ecTracKViewItemList(item: ICompany | IProduct | INews, itemListId: string, itemListName: string): Promise<void> {
    let data = this.getEcItem(item, itemListId, itemListName);
    if (item)
    this.ensureGMT()
    .subscribe(() => {
      this.queueSubject.next(data);
    });
  }

  async ecTrackViewItem(item: ICompany | IProduct | INews, itemListId: string, itemListName: string): Promise<void> {
    let data = this.getEcItem(item, itemListId, itemListName);
    this.ensureGMT()
    .subscribe(() => {
      this.resetEc();
      window.dataLayer.push({
        event: 'view_item',
        ecommerce: {
          currency: 'EUR',
          value: 0.5,
          items: [data]
        }
      });
    });
  }

  async ecTrackSelectItem(item: ICompany | IProduct | INews | IElasticSearchItem, itemListId: string, itemListName: string): Promise<void> {
    const data = this.getEcItem(item, itemListId, itemListName);
    this.ensureGMT()
    .subscribe(() => {
      this.resetEc();
      window.dataLayer.push({
        event: 'select_item',
        ecommerce: {
          item_list_id: itemListId,
          item_list_name: itemListName,
          items: [data]
        }
      });
    });
  }

  async ecTrackViewPromotion(item: ICompany | IProduct | INews, promotionId: string, promotionName: string): Promise<void> {
    const data = this.getEcItem(item, null, null, promotionId, promotionName);
    this.ensureGMT()
    .subscribe(() => {
      this.resetEc();
      window.dataLayer.push({
        event: 'view_promotion',
        ecommerce: {
          promotion_id: promotionId,
          promotion_name: promotionName,
          items: [data]
        }
      });
    });
  }

  async ecTrackSelectPromotion(item: ICompany | IProduct | INews, promotionId: string, promotionName: string): Promise<void> {
    const data = this.getEcItem(item, null, null, promotionId, promotionName);
    this.ensureGMT()
    .subscribe(() => {
      this.resetEc();
      window.dataLayer.push({
        event: 'select_promotion',
        ecommerce: {
          promotion_id: promotionId,
          promotion_name: promotionName,
          items: [data]
        }
      });
    });
  }

  async ecTrackAddRemoveToCart(item: ICompany | IProduct | INews, add: boolean): Promise<void> {
    const data = this.getEcItem(item);
    this.ensureGMT()
    .subscribe(() => {
      this.resetEc();
      window.dataLayer.push({
        event: add ? 'add_to_cart' : 'remove_from_cart',
        ecommerce: {
          currency: 'EUR',
          value: 1,
          items: [data]
        }
      });
    });
  }

  // evento personalizzato
  ecTrackRateItem(item: IProduct, rate: number): void {
    this.ensureGMT()
    .subscribe(() => {
      this.resetEc();
      window.dataLayer.push({
        event: 'rate_item',
        item_brand: item.companySlug,
        item_id: item._id,
        item_name: item.name,
        rate: rate
      });
    });
  }

  async trackCustomSearch(searchText: string): Promise<void> {
    this.resetEc();
    window.dataLayer.push({
      event: 'customSearch',
      customSearchInput: searchText
    });
  }

  private getEcItem(item: ICompany | IProduct | INews | IElasticSearchItem, itemListId?: string, itemListName?: string, promotionId?: string, promotionName?: string): GA4EcommerceItem {
    let data: GA4EcommerceItem = new GA4EcommerceItem();
    data.item_id = item._id;

    if (promotionId) {
      data.promotion_id = promotionId;
      data.promotion_name = promotionName;
    } else if (itemListId) {
      data.item_list_name = itemListName;
      data.item_list_id = itemListId;
    }

    // data.item_type = item.__typename;

    let categories: Array<string> = [];

    let __typename;
    if (item.hasOwnProperty('itemTypeName')) {
      __typename = (item as IElasticSearchItem).itemTypeName;
    } else {
      __typename = (item as any).__typename;
    }

    switch (item.__typename) {
      case 'ElasticSearchItem':
        item = item as IElasticSearchItem;
        switch(item.itemTypeName) {
          case EsType.company:
            data.item_name = item.company.fullName;
            data.item_brand = item.company.slug;
            categories = item.company.primaryPath.split('/');
          break;
          case EsType.product:
            data.item_name = item.title,
            data.item_variant = 'product';
            data.item_brand = item.company.slug;
            if (item.categories && item.categories.length > 0) {
              categories = item.categories;
            }
          break;
          case 'News':
            data.item_name = item.title;
            data.item_variant = 'news';
            if (item.company) {
              categories = item.company.primaryPath.split('/');
            }
          break;
        }
      break;
      case 'Company':
        item = item as ICompany;
        data.item_name = item.fullName;
        data.item_brand = item.slug;
        categories = item.primaryPath.split('/');
      break;
      case 'Product':
        item = item as IProduct;
        data.item_name = item.name,
        data.item_variant = 'product';
        data.item_brand = item.companySlug;
        if (item.categories && item.categories.length > 0) {
          categories = item.categories[0].path.split('/');
        }
      break;
      case 'News':
        item = item as INews;
        data.item_name = item.title;
        data.item_variant = 'news';
        if (item.categories && item.categories.length > 0) {
          categories = item.categories[0].path.split('/');
        }

      break;
    }
    let i = 0;
    for (const cat of categories) {
      if (!cat) {
        continue;
      }
      data['item_category' + (i === 0 ? '' : (i + 1).toString())] = cat;
      i++;
    }
    return data;
  }

  trackCustomEvent(event: string): void {
    this.ensureGMT()
    .subscribe(() => {
      this.resetEc();
      window.dataLayer.push({
        event: event
      })
    });
  }

  private resetEc(): void {
    window.dataLayer.push({ ecommerce: null });
  }

/*   private async loadCookieConsentModule(): Promise<void> {
    const consentCookie = this.cookieService.get('cookieconsent_status');
    this.hasConsent = consentCookie ? consentCookie === 'allow' : false;

    if (!this.hasConsent) {
      forkJoin([
        this.lazyLoadService.loadScript('cookie-consent-3.1.1.js', true),
        this.lazyLoadService.loadStyle('cookie-consent-3.1.1.css', true)
      ])
      .subscribe(async () => {
        const { CookieConsentComponent } = await import('app/shared/components/cookie-consent/cookie-consent.component')
        const componentRef = createComponent(CookieConsentComponent, {environmentInjector: this.appRef.injector});
        this.appRef.attachView(componentRef.hostView);
        // 3. Get DOM element from component
        const domElem = (componentRef.hostView as EmbeddedViewRef<any>)
        .rootNodes[0] as HTMLElement;
        // 4. Append DOM element to the body
        this.document.body.appendChild(domElem);
      });
    }
  } */

  ensureGMT(): Observable<boolean> {
    const cdn = environment.debugOn ? 'https://www.googletagmanager.com/gtm.js?id=' : 'https://load.gtm.expometals.net/uxrskhme.js?id=';
    return this.gmtIsLoaded ? of(true) :
    //   this.lazyLoadService.loadScript(`${cdn}${this.gmtId}`, false, 3500)
    this.lazyLoadService.loadScript(`${cdn}${this.gmtId}`, false, true)
    .pipe(
      tap(() => this.gmtIsLoaded = true),
      catchError(err =>  of(false)), // https://load.gtm.expometals.net é impostato a disallow * nel robots.txt stape, quindi viene ignorato da goolgebot e genera errore
      filter(loaded => loaded === true) // pensare se si può gestire meglio, magari aggiungendo un retry per l'utente comune, (non bot).
    );
  }
}
